import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
  ArrowLeft,
  Badge,
  Filter,
  GraduationCap,
  User as UserIcon,
  Loader2,
  Save,
} from 'lucide-react';
import { API_CONFIG } from '../config/api';
import type { FilterOptionsResponse } from '../config/api';
import { useTheme } from '../contexts/ThemeContext';
import '../styles/pages/EnterpriseGroupCreationPage.scss';
import SelectDropdown from '../components/SelectDropdown';
import { PSGCLocation, getRegions, getProvinces } from '../utils/locationApi';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000';

const getAuthHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem('token')}`,
  'Content-Type': 'application/json',
});

interface GroupData {
  _id: string;
  groupName: string;
  description: string;
  groupType: 'enterprise' | 'consultant';
  demographicFilters?: {
    region?: string;
    province?: string;
    city?: string;
    barangay?: string;
    ageRange?: string;
    school?: string;
    course?: string;
    yearLevel?: string;
  };
}

interface FolderData {
  _id: string;
  name: string;
  description: string;
}

type ItemData = GroupData | FolderData;

const isFolder = (item: any): item is FolderData => !('groupName' in item) && 'name' in item;
const isGroup = (item: any): item is GroupData => 'groupName' in item;

const EMPTY_OPTIONS: FilterOptionsResponse = {
  regions: [],
  provinces: [],
  cities: [],
  barangays: [],
  ageRanges: [],
  courses: [],
  yearLevels: [],
  schools: [],
};

async function fetchDemographicOptions(category: string): Promise<FilterOptionsResponse | null> {
  try {
    const qs = new URLSearchParams({ category });
    const res = await fetch(`${API_CONFIG.ENDPOINTS.USERS_ADMIN_FILTER_OPTIONS}?${qs}`, {
      headers: getAuthHeaders(),
    });
    if (!res.ok) return null;
    const json = await res.json();
    return json.success ? (json.data as FilterOptionsResponse) : null;
  } catch {
    return null;
  }
}

const EnterpriseGroupEditPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { isDarkMode } = useTheme();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [itemType, setItemType] = useState<'group' | 'folder'>('group');
  const [itemTitle, setItemTitle] = useState('');

  // Group data
  const [groupName, setGroupName] = useState('');
  const [description, setDescription] = useState('');
  const [activeCategory, setActiveCategory] = useState<'student' | 'general'>('student');

  // Demographic filters
  const [region, setRegion] = useState('all');
  const [province, setProvince] = useState('all');
  const [city, setCity] = useState('all');
  const [barangay, setBarangay] = useState('all');
  const [ageRange, setAgeRange] = useState('all');
  const [school, setSchool] = useState('all');
  const [course, setCourse] = useState('all');
  const [yearLevel, setYearLevel] = useState('all');

  // Filter options
  const [filterOptions, setFilterOptions] = useState<FilterOptionsResponse>(EMPTY_OPTIONS);
  const [filterLoading, setFilterLoading] = useState(false);

  // PSGC data
  const [psgcRegions, setPsgcRegions] = useState<PSGCLocation[]>([]);
  const [psgcProvinces, setPsgcProvinces] = useState<PSGCLocation[]>([]);
  const [psgcCities, setPsgcCities] = useState<PSGCLocation[]>([]);
  const [psgcBarangays, setPsgcBarangays] = useState<PSGCLocation[]>([]);
  const [psgcLoaded, setPsgcLoaded] = useState(false);

  // Load PSGC data on mount (only once) - must happen first!
  useEffect(() => {
    const loadPsgcData = async () => {
      try {
        const regions = await getRegions();
        const provinces = await getProvinces();

        // Fetch all cities from API
        const citiesResponse = await fetch('https://psgc.gitlab.io/api/cities-municipalities.json');
        const cities = citiesResponse.ok ? await citiesResponse.json() : [];

        // Fetch all barangays
        const barangaysResponse = await fetch('https://psgc.gitlab.io/api/barangays.json');
        const barangays = barangaysResponse.ok ? await barangaysResponse.json() : [];

        setPsgcRegions(regions || []);
        setPsgcProvinces(provinces || []);
        setPsgcCities(cities || []);
        setPsgcBarangays(barangays || []);
        setPsgcLoaded(true);
      } catch (err) {
        console.error('Failed to load PSGC data:', err);
        setPsgcLoaded(true);
      }
    };
    loadPsgcData();
  }, []);

  // Load item details - only load group data after PSGC is ready
  useEffect(() => {
    const loadItem = async () => {
      if (!id) {
        toast.error('Item ID is required');
        setTimeout(() => navigate('/panel/enterprise/groups'), 2000);
        return;
      }

      // Wait for PSGC data to load first
      if (!psgcLoaded) return;

      try {
        const response = await fetch(`${API_BASE}/api/group-folders/${id}`, {
          headers: getAuthHeaders(),
        });

        if (response.ok) {
          const data = await response.json();
          if (data.success) {
            setItemType('folder');
            const folder = data.data.folder || data.data;
            setItemTitle(folder.name || '');
            setGroupName(folder.name || '');
            setDescription(folder.description || '');
            setLoading(false);
            return;
          }
        }

        // Try to load as group
        const groupResponse = await fetch(`${API_BASE}/api/groups/${id}`, {
          headers: getAuthHeaders(),
        });

        if (groupResponse.ok) {
          const data = await groupResponse.json();
          if (data.success) {
            const group = data.data.group || data.data;
            setItemType('group');
            setItemTitle(group.groupName || '');
            setGroupName(group.groupName || '');
            setDescription(group.description || '');

            // Set demographic filters if available - this will trigger cascading effects
            if (group.demographicFilters) {
              setRegion(group.demographicFilters.region || 'all');
              setProvince(group.demographicFilters.province || 'all');
              setCity(group.demographicFilters.city || 'all');
              setBarangay(group.demographicFilters.barangay || 'all');
              setAgeRange(group.demographicFilters.ageRange || 'all');
              setSchool(group.demographicFilters.school || 'all');
              setCourse(group.demographicFilters.course || 'all');
              setYearLevel(group.demographicFilters.yearLevel || 'all');
            }

            setLoading(false);
            return;
          }
        }

        toast.error('Item not found');
        setTimeout(() => navigate('/panel/enterprise/groups'), 2000);
      } catch (error) {
        console.error('Failed to load item:', error);
        toast.error('Failed to load item details');
        setLoading(false);
      }
    };

    loadItem();
  }, [id, navigate, psgcLoaded]);

  // Load demographic options when category changes
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      if (itemType !== 'group') return;
      setFilterLoading(true);
      const data = await fetchDemographicOptions(activeCategory);
      if (!cancelled && data) setFilterOptions(data);
      if (!cancelled) setFilterLoading(false);
    };
    load();
    return () => { cancelled = true; };
  }, [activeCategory, itemType]);

  // Update displayed provinces when region changes
  useEffect(() => {
    let displayProvinces: string[] = [];
    if (region !== 'all' && psgcRegions.length > 0) {
      const regionObj = psgcRegions.find(r => {
        const displayName =
          r.regionName && r.name && r.regionName !== r.name
            ? `${r.regionName} (${r.name})`
            : r.regionName || r.name;
        return displayName === region;
      });
      if (regionObj) {
        displayProvinces = psgcProvinces
          .filter(p => p.regionCode === regionObj.code)
          .map(p => p.name)
          .filter(n => n)
          .sort();
      }
      setProvince('all');
      setCity('all');
      setBarangay('all');
    } else {
      setProvince('all');
      setCity('all');
      setBarangay('all');
    }
    setFilterOptions(prev => ({ ...prev, provinces: displayProvinces, cities: [], barangays: [] }));
  }, [region, psgcRegions, psgcProvinces]);

  // Update displayed cities when province changes
  useEffect(() => {
    let displayCities: string[] = [];
    if (province !== 'all' && region !== 'all' && psgcProvinces.length > 0) {
      const provinceObj = psgcProvinces.find(p => p.name === province);
      if (provinceObj) {
        displayCities = psgcCities
          .filter(c => c.provinceCode === provinceObj.code)
          .map(c => c.name)
          .filter(n => n)
          .sort();
      }
    } else if (region !== 'all' && filterOptions.provinces.length === 0) {
      // For regions without provinces, show cities from that region
      const regionObj = psgcRegions.find(r => {
        const displayName =
          r.regionName && r.name && r.regionName !== r.name
            ? `${r.regionName} (${r.name})`
            : r.regionName || r.name;
        return displayName === region;
      });
      if (regionObj) {
        displayCities = psgcCities
          .filter(c => c.regionCode === regionObj.code)
          .map(c => c.name)
          .filter(n => n)
          .sort();
      }
    }
    setCity('all');
    setBarangay('all');
    setFilterOptions(prev => ({ ...prev, cities: displayCities, barangays: [] }));
  }, [province, region, psgcCities, psgcProvinces, psgcRegions, filterOptions.provinces.length]);

  // Update displayed barangays when city changes
  useEffect(() => {
    let displayBarangays: string[] = [];
    if (city !== 'all' && psgcCities.length > 0) {
      const cityObj = psgcCities.find(c => c.name === city);
      if (cityObj) {
        displayBarangays = psgcBarangays
          .filter(b => b.cityCode === cityObj.code)
          .map(b => b.name)
          .filter(n => n)
          .sort();
      }
    }
    setBarangay('all');
    setFilterOptions(prev => ({ ...prev, barangays: displayBarangays }));
  }, [city, psgcCities, psgcBarangays]);

  // Save changes
  const handleSave = async () => {
    if (!groupName.trim()) {
      toast.error('Name is required');
      return;
    }

    if (!description.trim()) {
      toast.error('Description is required');
      return;
    }

    setSaving(true);

    try {
      let endpoint = '';
      let payload = {};

      if (itemType === 'folder') {
        endpoint = `/api/group-folders/${id}`;
        payload = {
          name: groupName.trim(),
          description: description.trim(),
        };
      } else {
        endpoint = `/api/groups/${id}`;
        payload = {
          groupName: groupName.trim(),
          description: description.trim(),
        };

        // Build demographic filters
        const demographicFilters: Record<string, string> = {};
        if (region !== 'all') demographicFilters.region = region;
        if (province !== 'all') demographicFilters.province = province;
        if (city !== 'all') demographicFilters.city = city;
        if (barangay !== 'all') demographicFilters.barangay = barangay;
        if (ageRange !== 'all') demographicFilters.ageRange = ageRange;
        if (activeCategory === 'student') {
          if (school !== 'all') demographicFilters.school = school;
          if (course !== 'all') demographicFilters.course = course;
          if (yearLevel !== 'all') demographicFilters.yearLevel = yearLevel;
        }
        (payload as any).demographicFilters = demographicFilters;
      }

      const response = await fetch(`${API_BASE}${endpoint}`, {
        method: 'PATCH',
        headers: getAuthHeaders(),
        body: JSON.stringify(payload),
      });

      const data = await response.json();

      if (data.success) {
        toast.success(
          `${itemType === 'folder' ? 'Folder' : 'Group'} updated successfully!`
        );
        setTimeout(() => navigate('/panel/enterprise/groups'), 2000);
      } else {
        toast.error(data.message || 'Failed to save changes');
      }
    } catch (error) {
      console.error('Save error:', error);
      toast.error('Network error while saving');
    } finally {
      setSaving(false);
    }
  };

  // Dropdown change handlers
  const handleProvinceChange = (val: string) => {
    setProvince(val);
    setCity('all');
    setBarangay('all');
    setSchool('all');
  };

  const handleCityChange = (val: string) => {
    setCity(val);
    setBarangay('all');
    setSchool('all');
  };

  if (loading) {
    return (
      <div className="enterprise-group-creation">
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '400px' }}>
          <Loader2 size={32} className="spin" style={{ color: 'var(--theme-primary-light)' }} />
        </div>
      </div>
    );
  }

  return (
    <div className="enterprise-group-creation">
      {/* Header with back button - Modern Design */}
      <div className="enterprise-group-creation__top-bar">
        <button className="btn-back" onClick={() => navigate('/panel/enterprise/groups')}>
          <ArrowLeft size={20} />
        </button>
        <div className="enterprise-group-creation__title">
          <h1>Edit {itemType === 'folder' ? 'Folder' : 'Group'}</h1>
          <p>{itemTitle}</p>
        </div>
      </div>

      {/* Main Content */}
      <div className="enterprise-group-creation__content">

        {/* Basic Information Section */}
        <section className="enterprise-group-creation__section">
          <div className="enterprise-group-creation__section-header">
            <div className="icon-badge">
              <Badge size={20} />
            </div>
            <div>
              <h2>{itemType === 'folder' ? 'Folder Information' : 'Group Identity'}</h2>
              <p>Update the name and description</p>
            </div>
          </div>
          <div className="enterprise-group-creation__form-grid">
            <div className="enterprise-group-creation__input-group">
              <label>{itemType === 'folder' ? 'Folder Name' : 'Group Name'} *</label>
              <input
                type="text"
                placeholder={itemType === 'folder' ? 'e.g., Regional Operations' : 'e.g., Q4 Regional Operations'}
                value={groupName}
                onChange={e => setGroupName(e.target.value)}
              />
            </div>
            <div className="enterprise-group-creation__input-group">
              <label>Description *</label>
              <input
                type="text"
                placeholder="Specify group scope and objectives"
                value={description}
                onChange={e => setDescription(e.target.value)}
              />
            </div>
          </div>
        </section>

        {/* Demographic Filters Section (Groups only) */}
        {itemType === 'group' && (
          <section className="enterprise-group-creation__section">
            <div className="enterprise-group-creation__section-header">
              <div className="icon-badge">
                <Filter size={20} />
              </div>
              <div>
                <h2>Who Can Join This Group?</h2>
                <p>Configure demographic visibility filters</p>
              </div>
            </div>

            <div className="enterprise-group-creation__filters-container">
              {/* Category select */}
              <div className="enterprise-group-creation__category-select">
                <label>Select Category</label>
                <div className="category-grid">
                  {(['student', 'general'] as const).map(cat => (
                    <div
                      key={cat}
                      className={`category-card ${activeCategory === cat ? 'category-card--active' : ''}`}
                      onClick={() => setActiveCategory(cat)}
                    >
                      <div className="icon-container">
                        {cat === 'student' ? (
                          <GraduationCap className="icon" />
                        ) : (
                          <UserIcon className="icon" />
                        )}
                      </div>
                      <div className="details">
                        <h3>{cat === 'student' ? 'Student' : 'General'}</h3>
                        <p>{cat === 'student' ? 'Filter by academic data' : 'Filter by demographic data'}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Dropdown Filters */}
              {filterLoading ? (
                <div className="enterprise-group-creation__loading">
                  <Loader2 size={20} className="icon spin" />
                  <span>Loading filter options…</span>
                </div>
              ) : (
                <>
                  {!filterOptions.regions?.length && (
                    <div className="enterprise-group-creation__info-note">
                      <strong>ℹ️ Note:</strong> Users match groups based on their demographic data. Set visibility criteria below.
                    </div>
                  )}
                  <div className="enterprise-group-creation__dropdown-grid">
                    {/* Region */}
                    <div className="enterprise-group-creation__filter-item">
                      <label>
                        Region{' '}
                        {filterOptions.regions.length > 0 && (
                          <span className="count-badge">
                            ({filterOptions.regions.length})
                          </span>
                        )}
                      </label>
                      <SelectDropdown
                        value={region}
                        onChange={setRegion}
                        options={[
                          { value: 'all', label: 'All regions' },
                          ...filterOptions.regions.map(r => ({ value: r, label: r })),
                        ]}
                        placeholder="Select region"
                      />
                    </div>

                    {/* Province - only show if region has provinces */}
                    {filterOptions.provinces.length > 0 && (
                      <div className="enterprise-group-creation__filter-item">
                        <label>
                          Province{' '}
                          {filterOptions.provinces.length > 0 && (
                            <span className="count-badge">
                              ({filterOptions.provinces.length})
                            </span>
                          )}
                        </label>
                        <SelectDropdown
                          value={province}
                          onChange={handleProvinceChange}
                          options={[
                            { value: 'all', label: 'All provinces' },
                            ...filterOptions.provinces.map(p => ({ value: p, label: p })),
                          ]}
                          placeholder="Select province"
                        />
                      </div>
                    )}

                    {/* Age Range */}
                    <div className="enterprise-group-creation__filter-item">
                      <label>Age Range</label>
                      <SelectDropdown
                        value={ageRange}
                        onChange={setAgeRange}
                        options={[
                          { value: 'all', label: 'All ages' },
                          ...filterOptions.ageRanges.map(a => ({ value: a, label: a })),
                        ]}
                        placeholder="Select age range"
                      />
                    </div>

                    {/* City */}
                    <div className="enterprise-group-creation__filter-item">
                      <label>
                        City{' '}
                        {filterOptions.cities.length > 0 && (
                          <span className="count-badge">
                            ({filterOptions.cities.length})
                          </span>
                        )}
                      </label>
                      <SelectDropdown
                        value={city}
                        onChange={handleCityChange}
                        options={[
                          { value: 'all', label: 'All cities' },
                          ...filterOptions.cities.map(c => ({ value: c, label: c })),
                        ]}
                        placeholder="Select city"
                      />
                    </div>

                    {/* School (student only) */}
                    {activeCategory === 'student' && (
                      <div className="enterprise-group-creation__filter-item">
                        <label>
                          School{' '}
                          {filterOptions.schools.length > 0 && (
                            <span className="count-badge">
                              ({filterOptions.schools.length})
                            </span>
                          )}
                        </label>
                        <SelectDropdown
                          value={school}
                          onChange={setSchool}
                          options={[
                            { value: 'all', label: 'All schools' },
                            ...filterOptions.schools.map(s => ({ value: s, label: s })),
                          ]}
                          placeholder="Select school"
                        />
                      </div>
                    )}

                    {/* Course (student only) */}
                    {activeCategory === 'student' && (
                      <div className="enterprise-group-creation__filter-item">
                        <label>
                          Course{' '}
                          {filterOptions.courses.length > 0 && (
                            <span className="count-badge">
                              ({filterOptions.courses.length})
                            </span>
                          )}
                        </label>
                        <SelectDropdown
                          value={course}
                          onChange={setCourse}
                          options={[
                            { value: 'all', label: 'All courses' },
                            ...filterOptions.courses.map(c => ({ value: c, label: c })),
                          ]}
                          placeholder="Select course"
                        />
                      </div>
                    )}

                  {/* Barangay */}
                  <div className="enterprise-group-creation__filter-item">
                    <label>Barangay</label>
                    <SelectDropdown
                      value={barangay}
                      onChange={setBarangay}
                      options={[
                        { value: 'all', label: 'All barangays' },
                        ...filterOptions.barangays.map(b => ({ value: b, label: b })),
                      ]}
                      placeholder="Select barangay"
                    />
                  </div>

                  {/* Year Level (student only) */}
                  {activeCategory === 'student' && (
                    <div className="enterprise-group-creation__filter-item">
                      <label>
                        Year Level{' '}
                        {filterOptions.yearLevels.length > 0 && (
                          <span className="count-badge">
                            ({filterOptions.yearLevels.length})
                          </span>
                        )}
                      </label>
                      <SelectDropdown
                        value={yearLevel}
                        onChange={setYearLevel}
                        options={[
                          { value: 'all', label: 'All year levels' },
                          ...filterOptions.yearLevels.map(y => ({ value: y, label: y })),
                        ]}
                        placeholder="Select year level"
                      />
                    </div>
                  )}
                  </div>
                </>
              )}
            </div>
          </section>
        )}
      </div>

      {/* Footer with buttons - Modern Design */}
      <footer className="enterprise-group-creation__footer">
        <div className="enterprise-group-creation__footer-content">
          <button className="btn-cancel" onClick={() => navigate('/panel/enterprise/groups')} disabled={saving}>
            Cancel
          </button>
          <button className="btn-create" onClick={handleSave} disabled={saving}>
            {saving ? (
              <>
                <Loader2 size={18} className="icon spin" /> Saving…
              </>
            ) : (
              <>
                <Save size={18} /> Save Changes
              </>
            )}
          </button>
        </div>
      </footer>
    </div>
  );
};

export default EnterpriseGroupEditPage;
